import React from 'react';
import type { ProgramSlideWebpageBlockV1 } from '../../types/programContentSlideCanvas';

export interface ProgramSlideWebpageBlockViewProps {
  block: ProgramSlideWebpageBlockV1;
  viewMode: boolean;
  /** URL·썸네일 없음 — 편집 모드에서 투명 플레이스홀더만 표시 */
  isPending?: boolean;
  embedFailed: boolean;
  onOpenUrl: () => void;
  onEmbedError: () => void;
}

const CODE_BLOCK_CSP_META =
  "<meta http-equiv=\"Content-Security-Policy\" content=\"default-src 'none'; script-src 'unsafe-inline' https://cdn.tailwindcss.com https://cdn.jsdelivr.net https://unpkg.com https://cdnjs.cloudflare.com; style-src 'unsafe-inline' https://fonts.googleapis.com https://cdn.jsdelivr.net https://unpkg.com https://cdnjs.cloudflare.com; img-src data: blob: https:; font-src data: https://fonts.gstatic.com https://cdn.jsdelivr.net https://cdnjs.cloudflare.com https:; connect-src 'none'; media-src 'none'; frame-src 'none'; form-action 'none'; base-uri 'none'; object-src 'none';\">";
const CODE_BLOCK_TRANSPARENT_STYLE =
  '<style>html,body{background:transparent!important;}body{margin:0;overflow:auto;}</style>';
const CODE_BLOCK_STORAGE_SHIM_SCRIPT = `<script>(function(){
  var createMemoryStorage = function () {
    var data = Object.create(null);
    return {
      getItem: function (key) {
        var k = String(key);
        return Object.prototype.hasOwnProperty.call(data, k) ? data[k] : null;
      },
      setItem: function (key, value) {
        data[String(key)] = String(value);
      },
      removeItem: function (key) {
        delete data[String(key)];
      },
      clear: function () {
        data = Object.create(null);
      },
      key: function (index) {
        var keys = Object.keys(data);
        return keys[index] ?? null;
      },
      get length() {
        return Object.keys(data).length;
      },
    };
  };
  var ensureStorage = function (name) {
    try {
      var s = window[name];
      if (!s) throw new Error('storage-missing');
      var probeKey = '__cp_storage_probe__';
      s.setItem(probeKey, '1');
      s.removeItem(probeKey);
    } catch (_err) {
      var memoryStorage = createMemoryStorage();
      try {
        Object.defineProperty(window, name, {
          configurable: true,
          get: function () {
            return memoryStorage;
          },
        });
      } catch (_defineErr) {
        // defineProperty가 막힌 런타임에서는 no-op; 사용자 코드에서 try/catch를 권장
      }
    }
  };
  ensureStorage('localStorage');
  ensureStorage('sessionStorage');
})();</script>`;
const buildCodeHeadInjection = (transparentBackground: boolean): string => {
  const bodyStyle = transparentBackground
    ? CODE_BLOCK_TRANSPARENT_STYLE
    : '<style>body{margin:0;overflow:auto;}</style>';
  return CODE_BLOCK_CSP_META + bodyStyle + CODE_BLOCK_STORAGE_SHIM_SCRIPT;
};

const buildCodeSrcDoc = (rawSourceCode: string, transparentBackground: boolean): string => {
  const source = rawSourceCode.trim();
  if (!source) return '';
  const headInjection = buildCodeHeadInjection(transparentBackground);
  const hasHtmlLikeWrapper =
    /<!doctype[\s>]/i.test(source) || /<html[\s>]/i.test(source) || /<body[\s>]/i.test(source);
  if (hasHtmlLikeWrapper) {
    if (/<head[\s>]/i.test(source)) {
      return source.replace(/<head(\s[^>]*)?>/i, (match) => `${match}${headInjection}`);
    }
    if (/<html[\s>]/i.test(source)) {
      return source.replace(
        /<html(\s[^>]*)?>/i,
        (match) => `${match}<head>${headInjection}</head>`
      );
    }
    return [
      '<!doctype html><html><head><meta charset="utf-8"/>',
      '<meta name="viewport" content="width=device-width,initial-scale=1"/>',
      headInjection,
      '</head><body>',
      rawSourceCode,
      '</body></html>',
    ].join('');
  }
  return [
    '<!doctype html><html><head><meta charset="utf-8"/>',
    '<meta name="viewport" content="width=device-width,initial-scale=1"/>',
    headInjection,
    '</head><body>',
    rawSourceCode,
    '</body></html>',
  ].join('');
};

const ProgramSlideWebpageBlockView: React.FC<ProgramSlideWebpageBlockViewProps> = ({
  block,
  viewMode,
  isPending = false,
  embedFailed,
  onOpenUrl,
  onEmbedError,
}) => {
  const mode = block.mode === 'code' ? 'code' : 'url';
  const url = block.url.trim();
  const title = block.title.trim() || url;
  const thumb = block.thumbSrc.trim();
  const sourceCode = (block.sourceCode ?? '').trim();
  const codeTransparentBackground = block.codeTransparentBackground !== false;
  const showCodeEmbed = mode === 'code' && !!sourceCode;
  const codeSrcDoc = showCodeEmbed
    ? buildCodeSrcDoc(block.sourceCode ?? '', codeTransparentBackground)
    : '';
  const showEmbed = Boolean(viewMode && block.embedSrc?.trim() && url && !embedFailed);
  const showCard = mode === 'url' && (!showEmbed || embedFailed);
  const cardClass =
    'cp-program-slide-webpage-card' + (showCard ? '' : ' cp-program-slide-webpage-card--hidden');

  const cardInner = (
    <>
      {thumb ? (
        <img className="cp-program-slide-webpage-thumb" src={thumb} alt="" draggable={false} />
      ) : (
        <div className="cp-program-slide-webpage-thumb cp-program-slide-webpage-thumb--empty" aria-hidden />
      )}
      <div className="cp-program-slide-webpage-card-meta">
        <span className="cp-program-slide-webpage-card-title">{title}</span>
        {url ? <span className="cp-program-slide-webpage-card-url">{url}</span> : null}
      </div>
    </>
  );

  if (isPending) {
    return (
      <div className="cp-program-slide-webpage-body cp-program-slide-webpage-body--edit">
        <div className="cp-program-slide-canvas-image-placeholder cp-program-slide-webpage-placeholder">
          {mode === 'code' ? 'HTML/CSS/JS 코드를 입력하세요' : '웹페이지 주소를 입력하세요'}
        </div>
      </div>
    );
  }

  return (
    <div
      className={'cp-program-slide-webpage-body' + (viewMode ? '' : ' cp-program-slide-webpage-body--edit')}
    >
      {showCodeEmbed ? (
        <iframe
          className={
            'cp-program-slide-webpage-iframe' +
            (codeTransparentBackground ? ' cp-program-slide-webpage-iframe--code' : '')
          }
          srcDoc={codeSrcDoc}
          title="소스코드 블럭"
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          tabIndex={viewMode ? undefined : -1}
        />
      ) : null}
      {showEmbed ? (
        <iframe
          className="cp-program-slide-webpage-iframe"
          src={block.embedSrc}
          title={title}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          onError={onEmbedError}
        />
      ) : null}
      {viewMode && mode === 'url' ? (
        <button
          type="button"
          className={cardClass}
          onClick={(e) => {
            e.stopPropagation();
            onOpenUrl();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={`${title} 새 탭에서 열기`}
          style={showCard ? undefined : { pointerEvents: 'none', visibility: 'hidden' }}
        >
          {cardInner}
        </button>
      ) : (
        <div className={cardClass + ' cp-program-slide-webpage-card--static'}>
          {mode === 'url' ? cardInner : null}
        </div>
      )}
    </div>
  );
};

export default ProgramSlideWebpageBlockView;
